// 총판 관계 관리 (distributor-desk) — FR-3/FR-4.
// distributor_relationships CRUD + 계약 연결(contracts.distributor_relationship_id 백필).
// 배치1 핵심: 여기서 관계를 등록해야 홈 보드(국가별 현황)에 데이터가 뜬다.
//
// 데이터 소유 규칙: countries/partners 는 읽기 전용 참조(useDistributorData).
// 이 페이지가 변경하는 relationships/contracts 는 자체 state 로 로드하고
// create/update 성공 후 재로드(refresh)해 최신화한다 (Layout 컨텍스트는 갱신 안 됨).
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { isSupabaseConfigured } from "@/lib/supabase";
import { contractsRepo, distributorRelationshipsRepo } from "@/lib/db";
import type { NewDistributorRelationship } from "@/lib/db";
import type {
  ContractRow,
  Country,
  DistributorExclusivity,
  DistributorRelationship,
  DistributorRelationshipStatus,
  Partner,
} from "@/types";
import { useDistributorData } from "./DistributorDeskLayout";
import {
  Badge,
  Btn,
  Card,
  cx,
  Disclaimer,
  EmptyState,
  Field,
  IconChevron,
  IconPin,
  IconUsers,
  Input,
  PageHeader,
  SectionLabel,
  Select,
  tdCls,
  tdNum,
  thCls,
  type Tone,
} from "./ui";

// ── labels / tones (DistributorHome 톤 계승) ─────────────────────────────────
const STATUS_OPTS: { id: DistributorRelationshipStatus; label: string }[] = [
  { id: "active", label: "활성" },
  { id: "dormant", label: "휴면" },
  { id: "terminated", label: "종료" },
];
const EXCL_OPTS: { id: DistributorExclusivity; label: string }[] = [
  { id: "exclusive", label: "독점" },
  { id: "non_exclusive", label: "비독점" },
  { id: "unset", label: "미지정" },
];
const STATUS_LABEL: Record<string, string> = Object.fromEntries(
  STATUS_OPTS.map((o) => [o.id, o.label]),
);
const EXCL_LABEL: Record<string, string> = Object.fromEntries(
  EXCL_OPTS.map((o) => [o.id, o.label]),
);
const STATUS_TONE: Record<string, Tone> = {
  active: "success",
  dormant: "warn",
  terminated: "neutral",
};
const EXCL_TONE: Record<string, Tone> = {
  exclusive: "accent",
  non_exclusive: "neutral",
  unset: "neutral",
};
const CONTRACT_STATUS_LABEL: Record<string, string> = {
  active: "활성",
  done: "완료",
  cancelled: "취소",
};

const COLS = 8;

const hasDistributorRole = (p: Partner): boolean =>
  Array.isArray(p.roles) && p.roles.includes("distributor");

/** 계약의 도착국 문자열이 관계 국가(country_id/명칭)와 매칭되는가 (대소문자 무시). */
function contractMatchesCountry(
  c: ContractRow,
  countryId: string,
  countries: Country[],
): boolean {
  const country = countries.find((x) => x.id === countryId);
  const targets = [countryId, country?.name_en, country?.name_ko]
    .filter((x): x is string => Boolean(x))
    .map((x) => x.trim().toLowerCase());
  const candidates = [c.destination_country, c.country_name]
    .filter((x): x is string => Boolean(x))
    .map((x) => x.trim().toLowerCase());
  return candidates.some((cand) => targets.includes(cand));
}

const isUniqueError = (msg: string): boolean =>
  /duplicate key|unique|23505/i.test(msg);

const HEAD = ["", "총판", "국가", "관계상태", "독점", "담당영업", "관계등급", "연결계약"];

const PAGE_SUBTITLE = "총판(파트너)×국가 관계 등록 · 계약 연결 · 상태·독점권 관리";
const DISCLAIMER_TEXT =
  "본 도구는 사전 정의된 항목만 취합·표시합니다. 최종 판단·확인은 담당자 책임입니다. 계약가·마진 등 대외비 데이터는 CF Access(SSO) 게이팅 이후 적재하십시오.";

export function RelationshipsPage(): JSX.Element {
  const d = useDistributorData();
  const { countries, partners } = d;

  // 이 페이지가 변경하는 모듈 데이터 — 자체 로드/재로드.
  const [relationships, setRelationships] = useState<DistributorRelationship[]>([]);
  const [contracts, setContracts] = useState<ContractRow[]>([]);
  const [loading, setLoading] = useState(true);
  const configured = isSupabaseConfigured();

  const reload = useCallback(async () => {
    const [rels, cons] = await Promise.all([
      distributorRelationshipsRepo.list().catch(() => [] as DistributorRelationship[]),
      contractsRepo.list().catch(() => [] as ContractRow[]),
    ]);
    setRelationships(rels);
    setContracts(cons);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!configured) {
        if (alive) setLoading(false);
        return;
      }
      await reload();
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [configured, reload]);

  const countryLabel = useCallback(
    (id: string): string => {
      const c = countries.find((x) => x.id === id);
      return c ? `${c.name_ko ?? c.name_en} (${c.id})` : id;
    },
    [countries],
  );
  const partnerName = useCallback(
    (id: string): string => partners.find((p) => p.id === id)?.company_name ?? "(미상 총판)",
    [partners],
  );

  // 국가 그룹 (라벨 오름차순, 그룹 내 총판명 오름차순).
  const groups = useMemo(() => {
    const byCountry = new Map<string, DistributorRelationship[]>();
    for (const r of relationships) {
      const key = r.country_id;
      if (!byCountry.has(key)) byCountry.set(key, []);
      byCountry.get(key)!.push(r);
    }
    return [...byCountry.entries()]
      .map(([countryId, rels]) => ({
        countryId,
        label: countryLabel(countryId),
        rels: [...rels].sort((a, b) =>
          partnerName(a.partner_id).localeCompare(partnerName(b.partner_id), "ko"),
        ),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "ko"));
  }, [relationships, countryLabel, partnerName]);

  const linkedCountByRel = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of contracts) {
      if (c.distributor_relationship_id) {
        m.set(c.distributor_relationship_id, (m.get(c.distributor_relationship_id) ?? 0) + 1);
      }
    }
    return m;
  }, [contracts]);

  if (!configured) {
    return (
      <div className="space-y-5">
        <PageHeader eyebrow="Relationships" title="총판 관계 관리" subtitle={PAGE_SUBTITLE} />
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">
          Supabase 미설정 — 관계 등록·조회가 비활성화됩니다.{" "}
          <code className="rounded bg-amber-100 px-1 py-0.5 text-[12px]">.env</code> 설정 후 이용하십시오.
        </div>
        <Disclaimer>{DISCLAIMER_TEXT}</Disclaimer>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Relationships" title="총판 관계 관리" subtitle={PAGE_SUBTITLE} />

      <CreateForm
        countries={countries}
        partners={partners}
        existing={relationships}
        onCreated={reload}
      />

      {loading ? (
        <Card className="px-4 py-16">
          <div className="text-center text-sm text-slate-400">불러오는 중…</div>
        </Card>
      ) : relationships.length === 0 ? (
        <EmptyState icon={<IconUsers className="h-8 w-8" />} title="등록된 총판 관계가 없습니다">
          위 폼에서 총판(파트너)×국가 관계를 등록하면 국가별 현황 보드에 취합됩니다.
        </EmptyState>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200/70">
                  {HEAD.map((h, i) => (
                    <th key={i} className={cx(thCls, i === HEAD.length - 1 && "text-right")}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groups.map((g) => (
                  <Fragment key={g.countryId}>
                    <tr className="bg-slate-50/70">
                      <td colSpan={COLS} className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <IconPin className="h-4 w-4 text-indigo-400" />
                          <span className="text-[13px] font-semibold text-slate-800">{g.label}</span>
                          <span className="text-[11px] text-slate-400">총판 {g.rels.length}</span>
                        </div>
                      </td>
                    </tr>
                    {g.rels.map((rel) => (
                      <RelationshipRow
                        key={rel.id}
                        rel={rel}
                        countries={countries}
                        partnerLabel={partnerName(rel.partner_id)}
                        linkedCount={linkedCountByRel.get(rel.id) ?? 0}
                        contracts={contracts}
                        onSaved={reload}
                      />
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Disclaimer>{DISCLAIMER_TEXT}</Disclaimer>
    </div>
  );
}

// ── 등록 폼 ──────────────────────────────────────────────────────────────────
function CreateForm({
  countries,
  partners,
  existing,
  onCreated,
}: {
  countries: Country[];
  partners: Partner[];
  existing: DistributorRelationship[];
  onCreated: () => Promise<void>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [partnerId, setPartnerId] = useState("");
  const [countryId, setCountryId] = useState("");
  const [status, setStatus] = useState<DistributorRelationshipStatus>("active");
  const [exclusivity, setExclusivity] = useState<DistributorExclusivity>("unset");
  const [owner, setOwner] = useState("");
  const [tier, setTier] = useState("");
  const [start, setStart] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  // 총판 역할 우선 정렬.
  const distPartners = useMemo(
    () =>
      partners
        .filter(hasDistributorRole)
        .sort((a, b) => a.company_name.localeCompare(b.company_name, "ko")),
    [partners],
  );
  const otherPartners = useMemo(
    () =>
      partners
        .filter((p) => !hasDistributorRole(p))
        .sort((a, b) => a.company_name.localeCompare(b.company_name, "ko")),
    [partners],
  );
  const sortedCountries = useMemo(
    () =>
      [...countries].sort((a, b) =>
        (a.name_ko ?? a.name_en).localeCompare(b.name_ko ?? b.name_en, "ko"),
      ),
    [countries],
  );

  const selPartner = partners.find((p) => p.id === partnerId) ?? null;
  const partnerLacksRole = selPartner != null && !hasDistributorRole(selPartner);
  const dupLocal =
    partnerId !== "" &&
    countryId !== "" &&
    existing.some((r) => r.partner_id === partnerId && r.country_id === countryId);

  const reset = (): void => {
    setPartnerId("");
    setCountryId("");
    setStatus("active");
    setExclusivity("unset");
    setOwner("");
    setTier("");
    setStart("");
    setNote("");
  };

  const submit = async (): Promise<void> => {
    setErr(null);
    setOk(false);
    if (!partnerId || !countryId) {
      setErr("총판과 국가를 선택하십시오.");
      return;
    }
    if (dupLocal) {
      setErr("이미 등록된 총판×국가 관계입니다. (동일 조합 중복 불가)");
      return;
    }
    const row: NewDistributorRelationship = {
      partner_id: partnerId,
      country_id: countryId,
      owner: owner.trim() || null,
      status,
      relationship_tier: tier.trim() || null,
      exclusivity,
      exclusive_territory: null,
      relationship_start: start || null,
      master_relationship_id: null,
      note: note.trim(),
    };
    setBusy(true);
    try {
      await distributorRelationshipsRepo.create(row);
      await onCreated();
      reset();
      setOk(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(
        isUniqueError(msg)
          ? "이미 등록된 총판×국가 관계입니다. (동일 조합 중복 불가)"
          : `등록 실패: ${msg}`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 bg-white px-4 py-3 text-left text-[13px] font-semibold text-slate-700 transition hover:bg-slate-50/60"
      >
        <span className="flex items-center gap-1.5">
          <span className="text-indigo-500">＋</span>총판 관계 등록
        </span>
        <span className="flex items-center gap-1 text-[11px] font-medium text-slate-400">
          {open ? "접기" : "펼치기"}
          <IconChevron className={cx("h-3.5 w-3.5 transition", open && "rotate-90")} />
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-slate-100 px-4 py-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="총판(파트너) *">
              <Select value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
                <option value="">— 선택 —</option>
                {distPartners.length > 0 && (
                  <optgroup label="총판 파트너">
                    {distPartners.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.company_name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {otherPartners.length > 0 && (
                  <optgroup label="기타 파트너">
                    {otherPartners.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.company_name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </Select>
            </Field>

            <Field label="국가 *">
              <Select value={countryId} onChange={(e) => setCountryId(e.target.value)}>
                <option value="">— 선택 —</option>
                {sortedCountries.map((c) => (
                  <option key={c.id} value={c.id}>
                    {(c.name_ko ?? c.name_en) + " (" + c.id + ")"}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="관계상태">
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value as DistributorRelationshipStatus)}
              >
                {STATUS_OPTS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="독점 여부">
              <Select
                value={exclusivity}
                onChange={(e) => setExclusivity(e.target.value as DistributorExclusivity)}
              >
                {EXCL_OPTS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="담당영업(본사)">
              <Input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="담당자명" />
            </Field>

            <Field label="관계등급">
              <Input value={tier} onChange={(e) => setTier(e.target.value)} placeholder="예: 핵심 / 신규" />
            </Field>

            <Field label="관계 시작일">
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </Field>

            <Field label="메모" className="sm:col-span-2 lg:col-span-3">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="비고 (선택)" />
            </Field>
          </div>

          {partnerLacksRole && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
              선택한 파트너에 ‘총판(distributor)’ 역할이 없습니다. 관계는 등록되지만 파트너 역할 확인을 권장합니다.
            </p>
          )}
          {dupLocal && (
            <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">
              이미 등록된 총판×국가 관계입니다. (동일 조합 중복 불가)
            </p>
          )}
          {err && (
            <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">
              {err}
            </p>
          )}
          {ok && !err && (
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700">
              등록되었습니다.
            </p>
          )}

          <div className="flex items-center gap-2">
            <Btn
              variant="primary"
              onClick={submit}
              disabled={busy || !partnerId || !countryId || dupLocal}
            >
              {busy ? "등록 중…" : "관계 등록"}
            </Btn>
            <Btn variant="ghost" onClick={reset} disabled={busy}>
              초기화
            </Btn>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── 관계 행 (펼침 = 편집 + 계약 연결) ────────────────────────────────────────
function RelationshipRow({
  rel,
  countries,
  partnerLabel,
  linkedCount,
  contracts,
  onSaved,
}: {
  rel: DistributorRelationship;
  countries: Country[];
  partnerLabel: string;
  linkedCount: number;
  contracts: ContractRow[];
  onSaved: () => Promise<void>;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  // 편집 폼 (rel 스냅샷 초기화).
  const [status, setStatus] = useState<DistributorRelationshipStatus>(rel.status);
  const [exclusivity, setExclusivity] = useState<DistributorExclusivity>(rel.exclusivity);
  const [owner, setOwner] = useState(rel.owner ?? "");
  const [tier, setTier] = useState(rel.relationship_tier ?? "");
  const [note, setNote] = useState(rel.note ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [linkingId, setLinkingId] = useState<string | null>(null);

  const dirty =
    status !== rel.status ||
    exclusivity !== rel.exclusivity ||
    (owner.trim() || "") !== (rel.owner ?? "") ||
    (tier.trim() || "") !== (rel.relationship_tier ?? "") ||
    (note.trim() || "") !== (rel.note ?? "");

  const linked = useMemo(
    () => contracts.filter((c) => c.distributor_relationship_id === rel.id),
    [contracts, rel.id],
  );
  const candidates = useMemo(
    () =>
      contracts.filter(
        (c) =>
          c.distributor_relationship_id == null &&
          contractMatchesCountry(c, rel.country_id, countries),
      ),
    [contracts, rel.country_id, countries],
  );

  const save = async (): Promise<void> => {
    setErr(null);
    setBusy(true);
    try {
      await distributorRelationshipsRepo.update(rel.id, {
        status,
        exclusivity,
        owner: owner.trim() || null,
        relationship_tier: tier.trim() || null,
        note: note.trim(),
      });
      await onSaved();
    } catch (e) {
      setErr(`저장 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const link = async (contractId: string): Promise<void> => {
    setErr(null);
    setLinkingId(contractId);
    try {
      await contractsRepo.update(contractId, { distributor_relationship_id: rel.id });
      await onSaved();
    } catch (e) {
      setErr(`계약 연결 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <>
      <tr
        className="cursor-pointer transition hover:bg-slate-50/60"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className={cx(tdCls, "w-6 text-slate-400")}>
          <IconChevron className={cx("h-4 w-4 transition", expanded && "rotate-90")} />
        </td>
        <td className={cx(tdCls, "whitespace-nowrap font-medium text-slate-800")}>{partnerLabel}</td>
        <td className={cx(tdCls, "whitespace-nowrap text-slate-500")}>{rel.country_id}</td>
        <td className={tdCls}>
          <Badge tone={STATUS_TONE[rel.status] ?? "neutral"}>
            {STATUS_LABEL[rel.status] ?? rel.status}
          </Badge>
        </td>
        <td className={tdCls}>
          <Badge tone={EXCL_TONE[rel.exclusivity] ?? "neutral"}>
            {EXCL_LABEL[rel.exclusivity] ?? rel.exclusivity}
          </Badge>
        </td>
        <td className={cx(tdCls, "whitespace-nowrap")}>{rel.owner || "—"}</td>
        <td className={cx(tdCls, "whitespace-nowrap")}>{rel.relationship_tier || "—"}</td>
        <td className={tdNum}>
          {linkedCount || <span className="text-slate-300">—</span>}
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={COLS} className="bg-slate-50/60 px-3 py-3">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* 편집 패널 */}
              <div onClick={(e) => e.stopPropagation()}>
                <Card className="space-y-3 p-4">
                  <SectionLabel>관계 편집</SectionLabel>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="관계상태">
                      <Select
                        value={status}
                        onChange={(e) => setStatus(e.target.value as DistributorRelationshipStatus)}
                      >
                        {STATUS_OPTS.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="독점 여부">
                      <Select
                        value={exclusivity}
                        onChange={(e) => setExclusivity(e.target.value as DistributorExclusivity)}
                      >
                        {EXCL_OPTS.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="담당영업">
                      <Input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="담당자명" />
                    </Field>
                    <Field label="관계등급">
                      <Input value={tier} onChange={(e) => setTier(e.target.value)} placeholder="예: 핵심" />
                    </Field>
                    <Field label="메모" className="col-span-2">
                      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="비고" />
                    </Field>
                  </div>
                  {err && (
                    <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">
                      {err}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <Btn variant="primary" onClick={save} disabled={busy || !dirty}>
                      {busy ? "저장 중…" : "변경 저장"}
                    </Btn>
                    <span className="text-[11px] text-slate-400">
                      시작일 {rel.relationship_start ?? "—"}
                    </span>
                  </div>
                </Card>
              </div>

              {/* 계약 연결 패널 */}
              <div onClick={(e) => e.stopPropagation()}>
                <Card className="space-y-3 p-4">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <SectionLabel>계약 연결</SectionLabel>
                    <span className="text-[11px] text-slate-400">(총판 계약 = 관계에 연결된 계약)</span>
                  </div>

                  <SectionLabel>연결된 계약</SectionLabel>
                  {linked.length === 0 ? (
                    <p className="text-[12px] text-slate-400">연결된 계약이 없습니다.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {linked.map((c) => (
                        <li
                          key={c.id}
                          className="flex items-center justify-between gap-2 rounded-lg border border-emerald-100 bg-emerald-50 px-2.5 py-1.5 text-[12px] text-slate-700"
                        >
                          <span className="truncate">
                            {c.name}
                            <span className="ml-1.5 text-slate-400">
                              [{c.destination_country || "—"} · {CONTRACT_STATUS_LABEL[c.status] ?? c.status}]
                            </span>
                          </span>
                          <Badge tone="success">연결됨</Badge>
                        </li>
                      ))}
                    </ul>
                  )}

                  <SectionLabel>연결 가능 (미연결 · 국가 일치)</SectionLabel>
                  {candidates.length === 0 ? (
                    <p className="text-[12px] text-slate-400">
                      연결 가능한 계약이 없습니다. (도착국이 이 관계 국가와 일치하고 아직 미연결인 계약만 표시)
                    </p>
                  ) : (
                    <ul className="space-y-1.5">
                      {candidates.map((c) => (
                        <li
                          key={c.id}
                          className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] text-slate-700"
                        >
                          <span className="truncate">
                            {c.name}
                            <span className="ml-1.5 text-slate-400">
                              [{c.destination_country || "—"} · {CONTRACT_STATUS_LABEL[c.status] ?? c.status}]
                            </span>
                          </span>
                          <Btn
                            variant="subtle"
                            onClick={() => link(c.id)}
                            disabled={linkingId === c.id}
                            className="shrink-0"
                          >
                            {linkingId === c.id ? "연결 중…" : "이 관계에 연결"}
                          </Btn>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
